<template>
	<header-block title="Spending Analysis"></header-block>
	<CustomerLoadWait :customer="ready" />

	<MonthSelector 
		v-model="selectedSlotKey"
		:slots="slots" />

	<transition>
		<v-row>
			<v-col>
				<AnalysisWidget v-if="ready"
					:spendingAnalysisStructured="spendingAnalysisStructured"
					:selectedSlotKey="selectedSlotKey"
					:formatter="formatter" 
					:title="false" />
			</v-col>
		</v-row>
	</transition>

	<transition>
		<v-row class="mt-2" v-if="recurringPayments && recurringPayments.payments.length > 0">
			<v-col>
				<InfoPanel :href="'/#/RecurringPayments'" :valueHref="'/#/RecurringPayments'"
					:icon="require('@/assets/coins.png')" title="Recurring Payments"
					subtitle="Stay on top of your recurring payments" valueTitle="Count"
					:value="recurringPayments.payments.length" />
			</v-col>
		</v-row>
	</transition>

	<transition>
		<v-row class="mt-2" v-if="bankInfo && bankInfo.accounts.length > 0 && bankInfo.untaggedTransactionsCurrentCycleCount > 0">
			<v-col>
				<InfoPanel class="shadowed"
					:href="'/#/Transaction/Bulk?from=' + bankInfo.currentCycleStartDate + '&to=' + bankInfo.today + '&includeNegative=true&includePositive=true'" 
					:valueHref="'/#/Transaction/Bulk?from=' + bankInfo.currentCycleStartDate + '&to=' + bankInfo.today + '&includeNegative=true&includePositive=true'" 
					:icon="require('@/assets/coins2.png')" 
					title="Tag Transactions" 
					subtitle="You have transactions to tag" 
					valueTitle="Count" 
					:value="bankInfo.untaggedTransactionsCurrentCycleCount" 
				/>
			</v-col>
		</v-row>
	</transition>

	<transition>
		<div v-if="ready" class="mt-4">
			<SpendingCategoryPanels
				:spendingAnalysisStructured="spendingAnalysisStructured"
				:selectedSlotKey="selectedSlotKey"
				:spendingPlans="spendingPlans"
				:formatter="formatter"
				:isFuturePlansCollapsed="isFuturePlansCollapsed"
				:isEveryDaySpendCollapsed="isEveryDaySpendCollapsed"
				:isCommittedCostsCollapsed="isCommittedCostsCollapsed"
				@categoryClicked="_handleCategoryClicked"
				@uncatagorisedTransactionsClicked="handleBulkCategorisationClick"
			/>
		</div>
	</transition>

	<DrawerPanel
		openHeight="85%" closedHeight="0px" :back="true" @goBack="handleDrawerGoBack" @closed="_handleDrawerClosed"
		ref="transactionDrawer" bottom="44px" :title="_drawerTitle" backgroundColor="white" openBackgroundColor="white">
		<!-- Page 1 -->
		<div v-if="!showMerchantTransactions && !showPlanManagement">
			<div class="centered">
				<h2>{{ selectedClassificationIncome > 0 ? 'Total Income' : 'Total Spend' }}</h2>
			</div>
			<div class="centered">
				<h1>{{ formatter.format(Math.abs(Math.round(selectedClassificationIncome > 0 ? selectedClassificationIncome : selectedClassificationSpend))) }}</h1>
			</div>
			<PlanInfoPanel v-if="selectedClassificationPlanAmount > 0 && selectedClassification"
				:alt="true"
				@click="_navigateToPlan(selectedClassification)" 
				:plan="selectedClassificationPlanAmount" 
				:spent="Math.abs(selectedClassificationSpend)" 
				:hideArrow="false" :key="totalPlan"
				title="Spending Plan"
				:formatter="formatter" 
				:iconPath="selectedClassification.iconPath" 
				:bgColour="selectedClassification.bgColour"/>
			<BarArrowButton v-else title="Create a new plan" :large="false" class="createNew" @click="_navigateToPlan(selectedClassification)"/>
			<WellPanel :data="categoryWellData" @merchantClicked="(m) => {_handleMerchantClicked(m)}" />
		</div>
		<!-- Page 2 -->
		<div v-if="showMerchantTransactions && !showPlanManagement">
			<div class="centered">
				<h2>Total Spend</h2>
			</div>
			<div class="centered">
				{{ formatter.format(Math.abs(Math.round(selectedMerchantSpend))) }}
			</div>
			<TransactionsList :transactions="merchantTransactions" :formatter="formatter" :today="today" :yesterday="yesterday" :showDate="true" :search="false" />
		</div>
		<!-- Page 3-->
		<div v-if="showPlanManagement">
			<SpendingPlanManagement
			@createPlan="(p) => { createPlan(p) }" 
			@updatePlan="(p) => { updatePlan(p) }" 
			@deletePlan="(p) => { deletePlan(p) }"
			:key="group" 
			:selectedPlan="selectedPlan" 
			:sections="sections"
			:managePlan="true"
			:categoriesPanel="categoriesPanel" :themeColour="themeColour" :formatter="formatter"/>
		</div>
	</DrawerPanel>

</template>

<script>

import Services from '../mixins/Services';
import Helpers from '../mixins/Helpers';
import SpendingAnalysis, { GRANDPARENT_CLASSIFICATIONS } from '../mixins/SpendingAnalysis';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import SpendingCategoryPanels from './elements/SpendingCategoryPanels.vue';
import AnalysisWidget from './elements/AnalysisWidget.vue';
import InfoPanel from './elements/InfoPanel.vue';
import DrawerPanel from './elements/DrawerPanel.vue';
import WellPanel from './elements/WellPanel.vue';
import TransactionsList from './elements/TransactionsList.vue';
import PlanInfoPanel from './elements/PlanInfoPanel.vue';
import BarArrowButton from './elements/BarArrowButton.vue';
import SpendingPlanManagement from './elements/SpendingPlanManagement.vue';
import MonthSelector from './elements/MonthSelector.vue';
import SpendingPlans from '../mixins/SpendingPlans';

export default {
	mixins: [Services, Helpers, SpendingAnalysis
	, SpendingPlans],
	components: {CustomerLoadWait, HeaderBlock, SpendingCategoryPanels, AnalysisWidget, InfoPanel, DrawerPanel, WellPanel
		, TransactionsList, PlanInfoPanel, BarArrowButton, 
		SpendingPlanManagement, MonthSelector
	},
	data() {
		return {		
			customer: null,
			recurringPayments: null,
			isEveryDaySpendCollapsed: true,
			isFuturePlansCollapsed: true,
			isCommittedCostsCollapsed: true,
			months: [],
			years: [],
			slots: [],
			allEveryDaySpend: [],
			allFuturePlans: [],
			allCommittedCosts: [],
			allIncome: [],
			selectedSlotKey: null,
			selectedTotals: [],
			selectedPlan: [],
			ready: false,
			selectedClassification: null,
			groupedTransactions: [],
			merchantWellData: [],
			merchantTransactions: [],
			closeDrawerNext: true,
			selectedClassificationPlanAmount: 0,
			selectedMerchantSpend: 0,
			sections: 
			[
				{
					fields: [
					{
							label: 'Plan amount',
							type: 'currency',
							value: null,
							required: true
						}
					]
				},
			],
		};
	},
	mounted: function() { 
		this.onLoad(); 
	},
	methods: {
		onLoad: async function() {
			this._getCustomer(() => {
				this._getBankingInfo(() => {
					if (this.bankInfo.spendingCycle.id == "00000000-0000-0000-0000-000000000000") {
						this.$router.replace('/setupspendinganalysis');
						return;
					}
					this._getRecurringPayments((res) => {
						this.recurringPayments = res;
					});
				});
				this._getSpendingAnalysisStructured(() => {
					this._getSpendingPlansFull(() => {
						this._extractActivePlans();
						this.slots = this.spendingAnalysisStructured.slotNames;
						this.selectedSlotKey = this.slots ? this.slots[this.slots.length - 2].key : '';
						this.ready = true;	
					});
				});
			});
		},

		updatePlan(p) {
			this._updatePlan(p, false);
			this.showPlanManagement = false, 
			this.closeDrawerNext = true
			this.selectedClassificationPlanAmount = p.plan.amount;
		},

		deletePlan(p) {
			this._deletePlan(p, false)
			this.showPlanManagement = false
			this.closeDrawerNext = true
			this.selectedClassificationPlanAmount = 0;
		},

		createPlan(p) {
			this.managePlan = false
			this._createPlan(p);
		},

		_resetSectionsForNewPlan() {
			// Reset the sections value to null for new plans
			this.sections[0].fields[0].value = null;
		},

		handleBulkCategorisationClick(classification) {
			var selectedSlot = this.slots.find(s => s.key === this.selectedSlotKey);
			var includePositive = classification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.INCOME;
			var includeNegative = classification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING || classification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND || classification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS;
			this.$router.push({ path: '/Transaction/Bulk', query: { from: selectedSlot.from, to: selectedSlot.to, includeNegative: includeNegative, includePositive: includePositive } });
		},

		handleDrawerGoBack() {
			if (this.showPlanManagement) {
				this.showPlanManagement = false;
				this.closeDrawerNext = true;
				// Reset sections when leaving plan management
				this.resetSections();
			}
			else
			if (this.showMerchantTransactions) {
				this.showMerchantTransactions = false;
				this.showPlanManagement = false;
				this.closeDrawerNext = true;
			}
			else {
				this.selectedClassificationPlanAmount = 0;
				this.selectedClassificationSpend = 0;
				this._setFieldValue('Plan amount', 0);
				this.$refs.transactionDrawer.closeDrawer();
			}
		},

		resetSections() {
			// Reset the sections to ensure no values persist from previous plans
			this.sections = [
				{
					fields: [
						{
							label: 'Plan amount',
							type: 'currency',
							value: null,
							required: true
						}
					]
				},
			];
		},

		toggleEveryDaySpending(isCollapsed) {
			this.isEveryDaySpendCollapsed = isCollapsed;
		},
		
		toggleFuturePlans(isCollapsed) {
			this.isFuturePlansCollapsed = isCollapsed;
		},

		toggleCommittedCosts(isCollapsed) {
			this.isCommittedCostsCollapsed = isCollapsed;
		}
	}
};

</script>

<style scoped>
.createNew {
	margin-top: 15px;
}

</style>